// File: src/components/pages/admin/ManageAdmins.jsx
//
// "Manage Admins" module — same structure as the existing "Manage Instructors"
// page, but scoped to role='ServiceProviderAdmin' accounts.
//
// Tabs: Active | Inactive | Suspended | Invited
// "Invite Admin" button opens InviteAccountModal pre-locked to the
// ServiceProviderAdmin role (the role selector only appears for the 'admin'
// system role — this page is 'admin'-only anyway, so the selector will show,
// but defaults can be steered via the `lockedRole` prop if desired).

import React, { useState } from 'react';
import { ShieldCheck, UserPlus } from 'lucide-react';
import AdminList from './AdminList';
import InviteAccountModal from '../../modals/InviteAccountModal';
// Path note: components/pages/admin/ManageAdmins.jsx
//   -> ../../modals/InviteAccountModal resolves to components/modals/InviteAccountModal

const TABS = [
  { key: 'active',    label: 'Active' },
  { key: 'inactive',  label: 'Inactive' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'invited',   label: 'Invited' },
];

const ManageAdmins = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [showInvite, setShowInvite] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="page-container" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ShieldCheck size={28} color="#7c3aed" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
              Manage Admins
            </h1>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
              Invite and manage ServiceProviderAdmin accounts
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowInvite(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.55rem 1.1rem', borderRadius: '0.5rem',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff', fontSize: '0.875rem', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 2px 6px rgba(118,75,162,0.3)'
          }}
        >
          <UserPlus size={16} /> Invite Admin
        </button>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: '0.25rem', marginBottom: '1.25rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.65rem 1.25rem',
              border: 'none', background: 'none',
              fontSize: '0.875rem', fontWeight: 600,
              fontFamily: 'inherit', cursor: 'pointer',
              color: activeTab === tab.key ? '#7c3aed' : '#6b7280',
              borderBottom: activeTab === tab.key ? '2.5px solid #7c3aed' : '2.5px solid transparent',
              marginBottom: '-2px',
              transition: 'color 0.15s, border-color 0.15s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── List ─────────────────────────────────────────────────────────── */}
      <AdminList key={`${activeTab}-${refreshKey}`} status={activeTab} />

      {/* ── Invite modal ─────────────────────────────────────────────────── */}
      {showInvite && (
        <InviteAccountModal
          onClose={() => setShowInvite(false)}
          onInvited={() => setRefreshKey(k => k + 1)}
          defaultRole="ServiceProviderAdmin"
        />
      )}

    </div>
  );
};

export default ManageAdmins;
